<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col>
        <hm-spinner v-if="!ready" />
        <b-form @submit.prevent="onSubmit">
          <b-card header="Manage Workforce Roles" v-show="ready">
            <Loading :active="busy" :is-full-page="false">
              <slot>
                <hm-spinner />
              </slot>
            </Loading>
            <hm-alert />
            <b-form-group
              label="Does the practice define roles and job duties for all job functions and keep written job descriptions that clearly set forth the qualifications?"
              label-class="font-weight-bold"
            >
              <hm-switch class="mx-1" v-model="role.writtenDesc" data-type="yesno" :disabled="busy" />
            </b-form-group>
            <b-form-group label="Do the roles and responsibilities assigned to practice workforce members support and enforce segregation of duties?" label-class="font-weight-bold">
              <p>
                Segregation of duties means: duties for determining, assigning, and enabling access to ePHI are performed by different people. In this way, no single person can establish an account, assign access credentials and turn
                on an individual’s access to ePHI. This built-in reliance on multiple people to enable access helps to reduce the risk of inappropriate access.
              </p>
              <hm-switch class="mx-1" v-model="role.segregation" data-type="yesno" :disabled="busy" />
            </b-form-group>
            <b-form-group label="What type of ePHI is each role / job duty authorized to access?" label-class="font-weight-bold">
              <p>
                In a HIPAA context, a role has less to do with job duties, and more to do with the level of information access the role requires to complete assigned job activities. Before you can assign a role to a workforce member,
                you must first define which information systems (e.g. clinical, billing, scheduling) each role is authorized to access&#59; making certain you limit access to the minimum necessary for a person to perform their job. This is
                often referred to as granting access on a &quot;need to know&quot; basis.
              </p>
              <hm-switch class="mx-1" v-model="role.globalAccess" data-type="check" :disabled="busy">
                Check this if the practice is &quot;very small&quot; and
                <u>all</u> staff have global access to ePHI
                <hm-popover placement="top" title="Small Practice">
                  Federal guidance states: &quot;In a very small provider office, all staff members may need to access all ePHI in their information system, since they may perform multiple functions. In this case, the covered entity might document the reasons for implementing
                  policies and procedures allowing this kind of global access. If the documented rationale is reasonable and appropriate, this may be an acceptable approach.&quot;
                </hm-popover>
              </hm-switch>
            </b-form-group>

            <div class="pt-2" v-show="!role.globalAccess">
              <b-table responsive bordered :items="roleMapRoleArray" :fields="columns">
                <template slot="cell(details)" slot-scope="data">
                  <b-form-group>
                    <div v-for="(value, key) in roleMap.Access" :key="key">
                      <hm-switch class="mx-1 p-1" size="sm" :value="key" :label="value" v-model="role.roles[data.item.key]" data-type="check" :disabled="busy" />
                    </div>
                  </b-form-group>
                </template>
              </b-table>
            </div>

            <div slot="footer">
              <b-row>
                <b-col class="text-right">
                  <b-button type="submit" variant="primary" class="px-4" :disabled="busy">Save</b-button>
                </b-col>
              </b-row>
            </div>
          </b-card>
        </b-form>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { workforceService } from '@/services';
import Enumerable from 'linq/linq.js';

export default {
  name: 'Practice.Roles',
  data() {
    return {
      role: {
        writtenDesc: false,
        segregation: false,
        globalAccess: true,
        roles: {
        }
      },
      columns: [{
        key: 'value',
        label: 'Role / Job Duty'
      }, {
        key: 'details',
        label: 'Settings'
      }],
      ready: false,
      busy: false,
      submitted: false,
    }
  },
  computed: {
    ...mapState('maps', ['roleMap']),
    ...mapState('maps', {
      roleMapRoleArray(state) {
        return Enumerable.from(state.roleMap.Roles).toArray();
      }
    })
  },
  methods: {
    ...mapActions({
      alertClear: 'alert/clear',
      alertSuccess: 'alert/success',
      alertError: 'alert/error',
      alertException: 'alert/exception',
      setProgress: 'progress/setFlagDone'
    }),
    onSubmit() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      this.$validator.validate()
        .then(valid => {
          if (valid) {
            return workforceService.setRole(this.role)
              .then(data => {
                this.setProgress('JobRolesSet');
                this.alertSuccess('Workforce roles saved successfully!');
              })
              .catch(err => {
                this.alertException({ err, message: 'Error saving roles.' });
              });
          } else {
            this.alertError('Please complete role information and submit again.');
          }
        })
        .finally(() => {
          this.busy = false;
        });
    }
  },
  created() {
    this.ready = false;

    Promise.all([
      this.$store.dispatch('maps/fetchRoleMap'),
      workforceService.getRole()
    ]).then((values) => {
      this.role = values[1];
    }).catch((err) => {
      this.alertException({ err, message: 'Error loading role map.' });
    }).finally(() => {
      this.ready = true;
    })
  }
}
</script>